
import { Component, Vue } from 'vue-property-decorator';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { isNil } from 'lodash';

import { sharedState } from '../../../../../framework/state';
import { postResource } from '../../../../../framework/client/resource';
import { getDatabaseOptions, getEnumerationOptions } from '../../../../../framework/service/options';
import { getQueryParameter } from '../../../../../framework/util/component_util';

import TextField from '../../../../../framework/fields/TextField.vue';
import SelectField from '../../../../../framework/fields/SelectField.vue';
import SwitchField from '../../../../../framework/fields/SwitchField.vue';
import IntegerField from '../../../../../framework/fields/IntegerField.vue';

import { PurchaseInvoiceRowMapping } from '../../../../../../common/application/model/PurchaseInvoiceRowMapping';
import { InvoiceRowType } from '../../../../../../common/application/model/enums/InvoiceRowType';
import { VendorContractType } from '../../../../../../common/application/model/enums/VendorContractType';
import { ApplicationResource } from '../../../../../../common/application/enumeration/ApplicationResource';
import { VendorType } from '../../../../../../common/application/model/enums/VendorType';
import { InvoicePeriodizationType } from '../../../../../../common/application/model/enums/InvoicePeriodizationType';
import { InvoicePeriodizationDayOffset } from '../../../../../../common/application/model/enums/InvoicePeriodizationDayOffset';

import { getVendorOptions } from '../../../../service/application_options_service';
import PeriodizationTypeSelector from '../../../shared/PeriodizationTypeSelector.vue';
import { InvoicePeriodizationOffsetDirection } from '../../../../../../common/application/model/enums/InvoicePeriodizationOffsetDirection';
import { PeriodizationModel } from '../../../../../../common/application/model/PeriodizationModel';

@Component({
    components: {
        IntegerField,
        SwitchField,
        SelectField,
        TextField,
        ValidationObserver,
        ValidationProvider,
        PeriodizationTypeSelector,
    },
})
export default class AddPurchaseInvoiceRowMapping extends Vue {
    readonly resourceType = ApplicationResource.PURCHASE_INVOICE_ROW_MAPPING;

    shared = sharedState;
    local = {
        row: {
            invoicePeriodizationType: InvoicePeriodizationType.UNSPECIFIED,
            invoicePeriodizationUseOffset: false,
            invoicePeriodizationOffsetDirection: InvoicePeriodizationOffsetDirection.ADD,
            invoicePeriodizationOffsetDays: 2,
        } as PurchaseInvoiceRowMapping,
        vendorTypeOptions: [] as { id: string | undefined; label: string }[],
        vendorOptions: [] as { id: string | undefined; label: string }[],
        invoiceRowTypeOptions: [] as { id: string | undefined; label: string }[],
        vendorContractTypeOptions: [] as { id: string | undefined; label: string }[],
    };

    get periodizationModel(): PeriodizationModel {
        return {
            type: this.local.row.invoicePeriodizationType,
            useOffset: this.local.row.invoicePeriodizationUseOffset ?? false,
            direction: this.local.row.invoicePeriodizationOffsetDirection ?? InvoicePeriodizationOffsetDirection.ADD,
            days: this.local.row.invoicePeriodizationOffsetDays ?? 0,
        };
    }

    onPeriodizationModelChanged(newModel: PeriodizationModel) {
        this.local.row = {
            ...this.local.row,
            invoicePeriodizationType: newModel.type,
            invoicePeriodizationUseOffset: newModel.useOffset,
            invoicePeriodizationOffsetDirection: newModel.direction,
            invoicePeriodizationOffsetDays: newModel.days,
        };
    }

    validatePeriodization(value: any) {
        if (!value || !value.type || value.type.toLowerCase() === 'unspecified') {
            return this.$t('message.invoicePeriodizationTypeRequired');
        }
        if (value.useOffset) {
            if (!value.direction) {
                return this.$t('message.offsetDirectionRequired');
            }
            if (!value.days || isNaN(value.days) || Number(value.days) <= 0) {
                return this.$t('message.offsetDaysRequired');
            }
        }
        return true;
    }

    async mounted() {
        this.local.vendorTypeOptions = getEnumerationOptions('VendorType', Object.keys(VendorType), this);
        this.local.vendorOptions = await getVendorOptions();
        this.local.invoiceRowTypeOptions = await getDatabaseOptions('InvoiceRowType', this);
        this.local.vendorContractTypeOptions = getEnumerationOptions(
            'VendorContractType',
            Object.keys(VendorContractType),
            this,
        );

        const vendorType = getQueryParameter('vendorType', this);
        if (!isNil(vendorType)) {
            this.local.row = {
                ...this.local.row,
                vendorType: vendorType as VendorType,
            };
        }
        const vendorProductPattern = getQueryParameter('vendorProductPattern', this);
        if (!isNil(vendorType)) {
            this.local.row = {
                ...this.local.row,
                vendorProductPattern: vendorProductPattern,
            };
        }
    }

    async add() {
        await postResource<PurchaseInvoiceRowMapping>(this.resourceType, this.local.row);
    }

    back() {
        this.$router.go(-1);
    }
}
